// OptionsStrategyPanel.jsx
// A modal that opens when an option row in PositionLedger is clicked.
//
// Props:
//   show          – boolean
//   onHide        – () => void
//   position      – formatted option position from OrderLedgerBuilder.formatPositionForTable()
//                   Fields: symbol, status, direction, quantity, option_type, strike, expiration,
//                           avg_entry_price, entry_fill_price, entry_filled_at, entry_submitted_at,
//                           entry_submitted_at_raw, entry_order_type, entry_limit_price, entry_slippage,
//                           exit_price, exit_fill_price, exit_filled_at, exit_order_type, exit_limit_price,
//                           exit_slippage, total_pnl, total_pnl_num, tp_limit, sl_stop, sl_limit, rr,
//                           hold_time, first_entry_time
//   matchedEval   – the PROCEED liveBars entry whose timestamp is closest to and before this
//                   position's entry. Fields: option_strategy, hedge_bias, hedge_size_pct,
//                   options_notes, confidence, confidence_bucket, regime_classification,
//                   volatility_feel, rr_preview, eval_new_target, eval_new_stop_loss, t

import React, { useState } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const v    = (x, fb = '—') => (x !== undefined && x !== null && x !== '' ? x : fb);
const fmtP = (x) => { const n = parseFloat(x); return isNaN(n) || n === 0 ? '—' : `$${n.toFixed(2)}`; };
const fmtN = (x, d = 2)   => { const n = parseFloat(x); return isNaN(n) ? '—' : n.toFixed(d); };

// ─────────────────────────────────────────────────────────────────────────────
// TOKENS  — BRD design system (matches platform palette)
// ─────────────────────────────────────────────────────────────────────────────
const T = {
  bg:      '#08101E',
  card:    '#0C1525',
  surface: '#101B2E',
  s3:      '#0e1828',
  border:  '#1E2D42',
  border2: '#2a3d56',
  text:    '#E8EDF5',
  muted:   '#6A7D95',
  subtle:  '#1E2D42',
  body:    '#B8C8DC',
  green:   '#00E896', greenBg: 'rgba(0,232,150,0.10)',   greenBdr: 'rgba(0,232,150,0.30)',
  amber:   '#FFB340', amberBg: 'rgba(255,179,64,0.10)',  amberBdr: 'rgba(255,179,64,0.30)',
  blue:    '#4488FF', blueBg:  'rgba(68,136,255,0.10)',  blueBdr:  'rgba(68,136,255,0.30)',
  purple:  '#9966FF', purpleBg:'rgba(153,102,255,0.10)', purpleBdr:'rgba(153,102,255,0.30)',
  red:     '#FF4466', redBg:   'rgba(255,68,102,0.10)',  redBdr:   'rgba(255,68,102,0.30)',
  cyan:    '#00C8FF', cyanBg:  'rgba(0,200,255,0.10)',   cyanBdr:  'rgba(0,200,255,0.30)',
  slate:   '#6A7D95', slateBg: 'rgba(106,125,149,0.08)', slateBdr: 'rgba(106,125,149,0.20)',
  font:    "'DM Mono', monospace",
};

const STRAT = {
  LONG_PREMIUM: { c: T.blue,   bg: T.blueBg,   b: T.blueBdr   },
  DEBIT_SPREAD: { c: T.purple, bg: T.purpleBg,  b: T.purpleBdr },
  SKIP_OPTIONS: { c: T.slate,  bg: T.slateBg,   b: T.slateBdr  },
  CREDIT_SPREAD:{ c: T.amber,  bg: T.amberBg,   b: T.amberBdr  },
  IRON_CONDOR:  { c: T.cyan,   bg: T.cyanBg,    b: T.cyanBdr   },
};
const stratStyle = (s) => STRAT[s] || { c: T.muted, bg: T.slateBg, b: T.slateBdr };

// Day type → color (BRD-2)
const dayTypeColor = (dt) => ({
  TREND_DAY:        T.green,
  STRONG_TREND_DAY: T.green,
  EVENT_TREND:      T.cyan,
  EVENT_DAY:        T.cyan,
  WEAK_TREND_DAY:   '#88D400',
  RANGE_DAY:        T.blue,
  SIDEWAYS_DAY:     T.purple,
  CHOPPY_DAY:       T.red,
  VOLATILE_DAY:     T.amber,
})[dt] || T.muted;

// Extract regime context from a matched eval bar (BRD-2)
// Reads regime_summary shortcut first (available on PROCEED bars), then full regime_context
const extractRc = (matchedEval) => {
  if (!matchedEval) return null;
  const story = matchedEval.eval_agent_story || {};
  const msnap = story.market_snapshot || {};
  const rc    = msnap.regime_context  || {};
  const rs    = story.regime_summary  || {};
  return {
    dayType:              rs.day_type              || rc.day_type              || null,
    dayTypeReason:        rc.day_type_reason       || null,
    entryType:            rs.entry_type            || rc.entry_type            || null,
    entryAllowed:         rc.entry_allowed         ?? rs.entry_allowed         ?? null,
    entryBlockReason:     rc.entry_block_reason    || null,
    earlyEntryWindowOpen: rc.early_entry_window_open ?? true,
    priorProceedTaken:    rc.prior_proceed_taken   ?? false,
    eventDay:             rc.event_day_detected    ?? false,
    der:                  rc.der                   ?? null,
    ci14:                 rc.ci_14                 ?? null,
    ci6:                  rc.ci_6                  ?? null,
    volatilityCtx:        rs.volatility_context    || rc.volatility_context    || null,
    volatilityReason:     rc.volatility_context_reason || null,
    rrFloor:              rs.rr_floor              ?? rc.rr_floor              ?? null,
    rrPreview:            msnap.rr_preview         ?? null,
    scaleCap:             rs.scale_cap             ?? rc.scale_cap             ?? null,
    stopFloor:            rs.stop_floor_pct        ?? rc.stop_floor_pct        ?? null,
    cooldownActive:       msnap.cooldown_active    ?? false,
    stabilityScore:       msnap.stability_score    ?? null,
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// PRIMITIVES
// ─────────────────────────────────────────────────────────────────────────────
const Chip = ({ children, c = T.muted, bg = T.slateBg, b = T.slateBdr }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', padding: '3px 10px',
    borderRadius: '4px', border: `1px solid ${b}`,
    backgroundColor: bg, color: c,
    fontSize: '10px', fontWeight: 600, whiteSpace: 'nowrap',
    fontFamily: T.font, letterSpacing: '0.04em',
  }}>{children}</span>
);

const PnLColor = (num) => parseFloat(num) >= 0 ? T.green : T.red;

const FieldRow = ({ label, value, accent, mono }) => (
  <div style={{
    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
    gap: '8px', padding: '5px 0', borderBottom: `1px solid ${T.border}`,
  }}>
    <span style={{ fontSize: '11px', color: T.muted, flexShrink: 0, fontFamily: T.font }}>{label}</span>
    <span style={{
      fontSize: '11px', color: accent || T.text, fontWeight: accent ? 600 : 400,
      textAlign: 'right', fontFamily: mono ? T.font : T.font,
    }}>{v(value)}</span>
  </div>
);

const SLabel = ({ children }) => (
  <div style={{
    fontSize: '9px', color: T.muted, textTransform: 'uppercase', fontFamily: T.font,
    letterSpacing: '0.08em', marginBottom: '8px', marginTop: '16px',
  }}>{children}</div>
);

const Box = ({ children, style }) => (
  <div style={{
    borderRadius: '6px', border: `1px solid ${T.border}`,
    backgroundColor: T.surface, padding: '10px 12px', ...style,
  }}>{children}</div>
);

const Stat = ({ label, value, accent, sub }) => (
  <Box>
    <div style={{ fontSize: '9px', color: T.muted, marginBottom: '3px', fontFamily: T.font, letterSpacing: '0.06em' }}>{label}</div>
    <div style={{ fontSize: '13px', fontWeight: 700, color: accent || T.text, fontFamily: T.font }}>{v(value)}</div>
    {sub && <div style={{ fontSize: '9px', color: T.muted, marginTop: '2px', fontFamily: T.font, lineHeight: 1.4 }}>{sub}</div>}
  </Box>
);

const TabBtn = ({ label, active, onClick }) => (
  <button onClick={onClick} style={{
    padding: '8px 16px', fontSize: '10px', cursor: 'pointer', border: 'none',
    fontWeight: active ? 700 : 400, color: active ? T.text : T.muted,
    backgroundColor: 'transparent', fontFamily: T.font, letterSpacing: '0.06em',
    borderBottom: active ? `2px solid ${T.cyan}` : '2px solid transparent',
  }}>{label.toUpperCase()}</button>
);

// ─────────────────────────────────────────────────────────────────────────────
// TAB: CONTEXT  (eval fields — why this option was chosen)
// ─────────────────────────────────────────────────────────────────────────────
const ContextTab = ({ position, matchedEval }) => {
  const story  = matchedEval?.eval_agent_story || {};
  const llm    = story.chart_analysis?.llm_full_response || {};
  const dec    = story.decision || {};

  const strat    = llm.option_strategy;
  const bias     = llm.hedge_bias;
  const sc       = stratStyle(strat);
  const optNotes = llm.options_notes;

  // BRD-2: read regime_context + regime_summary directly — no more regex
  const rc = extractRc(matchedEval);

  // Fallback regime from old field path if BRD-2 fields absent
  const legacyRegime = llm.regime_analysis?.regime_classification || null;

  // Confidence from decision block (post-risk-adjusted)
  const confidence = dec.confidence ?? llm.confidence;
  const confBucket = dec.confidence_bucket ?? '';
  const target     = dec.new_target   || llm.target_price;
  const stopLoss   = dec.new_stop_loss;

  // R:R comparison
  const rrFloor   = rc?.rrFloor;
  const rrPreview = rc?.rrPreview;
  const rrBelow   = rrFloor != null && rrPreview != null && rrPreview < rrFloor;

  if (!matchedEval) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: T.slate, fontSize: '12px', fontFamily: T.font }}>
        No PROCEED evaluation found before this position's entry time.
        <div style={{ marginTop: '8px', fontSize: '11px', color: T.muted }}>
          Entry at: {v(position.entry_submitted_at)}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Strategy + Entry type + Hedge chips */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '14px' }}>
        <Chip c={sc.c} bg={sc.bg} b={sc.b}>STRAT: {v(strat, 'UNKNOWN')}</Chip>

        {rc?.entryType && (
          <Chip
            c={rc.entryType === 'EARLY_ENTRY' ? T.amber : T.cyan}
            bg={rc.entryType === 'EARLY_ENTRY' ? T.amberBg : T.cyanBg}
            b={rc.entryType === 'EARLY_ENTRY' ? T.amberBdr : T.cyanBdr}
          >
            {rc.entryType.replace(/_/g, ' ')}
          </Chip>
        )}

        <Chip
          c={bias && bias !== 'NONE' ? T.amber : T.muted}
          bg={bias && bias !== 'NONE' ? T.amberBg : T.slateBg}
          b={bias && bias !== 'NONE' ? T.amberBdr : T.slateBdr}
        >
          HEDGE: {v(bias, 'NONE')}
          {bias && bias !== 'NONE' && llm.hedge_size_pct != null
            ? ` (${Math.round(llm.hedge_size_pct * 100)}%)` : ''}
        </Chip>

        {rc?.eventDay && (
          <Chip c={T.cyan} bg={T.cyanBg} b={T.cyanBdr}>EVENT DAY</Chip>
        )}
        {rc?.priorProceedTaken && (
          <Chip c={T.amber} bg={T.amberBg} b={T.amberBdr}>CAPITAL COMMITTED</Chip>
        )}
        {rc?.cooldownActive && (
          <Chip c={T.red} bg={T.redBg} b={T.redBdr}>COOLDOWN</Chip>
        )}
      </div>

      {/* Entry blocked warning */}
      {rc?.entryAllowed === false && (
        <Box style={{ marginBottom: '14px', border: `1px solid ${T.amber}50`, backgroundColor: T.amberBg }}>
          <div style={{ fontSize: '9px', color: T.amber, fontFamily: T.font, fontWeight: 700, marginBottom: '4px', letterSpacing: '0.06em' }}>ENTRY BLOCKED AT EVAL TIME</div>
          <div style={{ fontSize: '11px', color: T.body, fontFamily: T.font, lineHeight: 1.6 }}>
            {rc.entryBlockReason || 'Regime conditions did not meet entry criteria'}
          </div>
        </Box>
      )}

      {/* AI Rationale */}
      {optNotes && (
        <Box style={{ marginBottom: '14px' }}>
          <div style={{ fontSize: '9px', color: T.muted, marginBottom: '5px', fontFamily: T.font, letterSpacing: '0.06em' }}>AI RATIONALE</div>
          <div style={{ fontSize: '11px', color: T.body, lineHeight: '1.6', fontFamily: T.font }}>
            {optNotes}
          </div>
        </Box>
      )}

      {/* Session Context — BRD-2 regime fields */}
      <SLabel>Session Context at Entry</SLabel>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '12px' }}>
        {/* Day Type — colored by type */}
        <Stat
          label="Day Type"
          value={rc?.dayType ? rc.dayType.replace(/_/g, ' ') : (legacyRegime || '—')}
          accent={rc?.dayType ? dayTypeColor(rc.dayType) : T.muted}
          sub={rc?.dayTypeReason || null}
        />

        {/* Volatility — full sentence if available */}
        <Stat
          label="Volatility"
          value={rc?.volatilityCtx || '—'}
          sub={rc?.volatilityReason || null}
        />

        {/* Confidence */}
        <Stat
          label="Confidence"
          value={confidence != null
            ? `${typeof confidence === 'number' ? Math.round(confidence * 100) + '%' : confidence}${confBucket ? ` (${confBucket})` : ''}`
            : '—'}
        />

        {/* R:R preview vs floor */}
        <Stat
          label="R:R Preview"
          value={rrPreview != null ? fmtN(rrPreview, 3) : '—'}
          accent={rrBelow ? T.red : rrPreview != null ? T.green : undefined}
          sub={rrFloor != null
            ? `floor ${fmtN(rrFloor, 2)} — ${rrBelow ? 'BELOW FLOOR' : 'above floor'}`
            : null}
        />

        {/* Entry Type */}
        <Stat
          label="Entry Type"
          value={rc?.entryType ? rc.entryType.replace(/_/g, ' ') : '—'}
          accent={rc?.entryType === 'EARLY_ENTRY' ? T.amber : rc?.entryType ? T.cyan : undefined}
        />

        {/* DER */}
        <Stat
          label="Trend Eff (DER)"
          value={rc?.der != null ? fmtN(rc.der, 3) : '—'}
          accent={rc?.der != null
            ? rc.der >= 0.7 ? T.green : rc.der >= 0.4 ? T.amber : T.red
            : undefined}
        />

        {/* AI Target */}
        <Stat label="AI Target" value={target ? fmtP(target) : '—'} accent={T.green} />

        {/* AI Stop */}
        <Stat label="AI Stop" value={stopLoss ? fmtP(stopLoss) : '—'} accent={T.red} />

        {/* Chop Index */}
        {rc?.ci14 != null && (
          <Stat
            label="Chop Index 14 / 6"
            value={`${fmtN(rc.ci14, 1)} / ${rc.ci6 != null ? fmtN(rc.ci6, 1) : '—'}`}
            accent={rc.ci14 < 38.2 ? T.green : rc.ci14 > 61.8 ? T.red : T.amber}
            sub={rc.ci14 < 38.2 ? 'trending' : rc.ci14 > 61.8 ? 'choppy' : 'transitional'}
          />
        )}
      </div>

      {/* Active Risk Params row — scale cap, stop floor */}
      {(rc?.scaleCap != null || rc?.stopFloor != null) && (
        <>
          <SLabel>Active Risk Parameters</SLabel>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
            {rc.scaleCap  != null && (
              <Box style={{ flex: '0 0 auto' }}>
                <div style={{ fontSize: '9px', color: T.muted, marginBottom: '3px', fontFamily: T.font }}>SCALE CAP</div>
                <div style={{ fontSize: '13px', fontWeight: 700, color: T.blue, fontFamily: T.font }}>{rc.scaleCap}×</div>
              </Box>
            )}
            {rc.stopFloor != null && (
              <Box style={{ flex: '0 0 auto' }}>
                <div style={{ fontSize: '9px', color: T.muted, marginBottom: '3px', fontFamily: T.font }}>STOP FLOOR</div>
                <div style={{ fontSize: '13px', fontWeight: 700, color: T.amber, fontFamily: T.font }}>{(rc.stopFloor * 100).toFixed(1)}%</div>
              </Box>
            )}
            {rc?.stabilityScore != null && (
              <Box style={{ flex: '0 0 auto' }}>
                <div style={{ fontSize: '9px', color: T.muted, marginBottom: '3px', fontFamily: T.font }}>STABILITY</div>
                <div style={{ fontSize: '13px', fontWeight: 700, fontFamily: T.font,
                  color: rc.stabilityScore >= 0.7 ? T.green : rc.stabilityScore >= 0.4 ? T.amber : T.red }}>
                  {Math.round(rc.stabilityScore * 100)}
                </div>
              </Box>
            )}
          </div>
        </>
      )}

      {/* Timestamp note */}
      <div style={{ fontSize: '10px', color: T.muted, textAlign: 'right', fontFamily: T.font }}>
        Eval fired: {v(matchedEval?.t ? new Date(matchedEval.t).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }) : null)}
        {' '}· Entry submitted: {v(position.entry_submitted_at)}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// TAB: EXECUTION  (position order fields)
// ─────────────────────────────────────────────────────────────────────────────
const ExecutionTab = ({ position }) => {
  const isClosed  = position.status === 'closed';
  const pnlNum    = parseFloat(position.total_pnl_num || 0);
  const slipColor = (v) => parseFloat(v || 0) > 0 ? T.red : T.green;

  return (
    <div>
      <SLabel>Entry</SLabel>
      <FieldRow label="Submitted"  value={position.entry_submitted_at} />
      <FieldRow label="Fill time"  value={position.entry_filled_at} />
      <FieldRow label="Order type" value={v(position.entry_order_type)} />
      <FieldRow label="Limit"      value={fmtP(position.entry_limit_price)} />
      <FieldRow label="Fill price" value={fmtP(position.entry_fill_price || position.avg_entry_price)} accent={T.blue} />
      <FieldRow label="Slippage"   value={fmtN(position.entry_slippage, 4)} accent={slipColor(position.entry_slippage)} />
      <FieldRow label="Qty"        value={v(position.quantity)} />

      {isClosed && (
        <>
          <SLabel>Exit</SLabel>
          <FieldRow label="Fill time"  value={v(position.exit_filled_at)} />
          <FieldRow label="Order type" value={v(position.exit_order_type)} />
          <FieldRow label="Limit"      value={fmtP(position.exit_limit_price)} />
          <FieldRow label="Fill price" value={fmtP(position.exit_fill_price || position.exit_price)} accent={T.amber} />
          <FieldRow label="Slippage"   value={fmtN(position.exit_slippage, 4)} accent={slipColor(position.exit_slippage)} />
        </>
      )}

      <SLabel>Result</SLabel>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '12px' }}>
        <Stat label="P&L"      value={`$${fmtN(position.total_pnl_num)}`} accent={PnLColor(pnlNum)} />
        <Stat label="Hold time" value={v(position.hold_time)} />
        <Stat label="R:R"      value={fmtN(position.rr, 3)} />
      </div>

      {(position.tp_limit || position.sl_stop) && (
        <>
          <SLabel>Bracket</SLabel>
          <FieldRow label="Take profit" value={fmtP(position.tp_limit)} accent={T.green} />
          <FieldRow label="Stop loss"   value={fmtP(position.sl_stop)}  accent={T.red} />
          {position.sl_limit && <FieldRow label="Stop limit" value={fmtP(position.sl_limit)} accent={T.red} />}
        </>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// TAB: MANAGEMENT  (rules derived from strategy + hedge_bias + BRD-2 session state)
// ─────────────────────────────────────────────────────────────────────────────
const ManagementTab = ({ matchedEval }) => {
  const llm      = matchedEval?.eval_agent_story?.chart_analysis?.llm_full_response || {};
  const strat    = llm.option_strategy;
  const bias     = llm.hedge_bias || 'NONE';
  const hedgePct = llm.hedge_size_pct || 0;

  const rc = extractRc(matchedEval);

  const rules = [
    { label: 'Compression Lockout', on: strat === 'DEBIT_SPREAD',                         ac: T.green  },
    { label: 'No-Expansion Exit',   on: strat === 'DEBIT_SPREAD' || bias === 'REDUCE_SIZE', ac: T.amber  },
    { label: 'Convert to Spread',   on: bias === 'CONVERT_TO_SPREAD',                       ac: T.purple },
    { label: 'Reduce Size',         on: bias === 'REDUCE_SIZE',                             ac: T.amber  },
    { label: 'Cooldown Active',     on: !!(rc?.cooldownActive),                             ac: T.red    },
  ];

  const pyramidNote = () => {
    if (!strat)                      return 'No strategy data — pyramiding behavior undetermined.';
    if (strat === 'SKIP_OPTIONS')    return 'No options active.';
    if (strat === 'DEBIT_SPREAD')    return 'DEBIT_SPREAD: pyramid adds disabled. Spreads cap upside; adding size increases leg risk.';
    if (strat === 'LONG_PREMIUM') {
      if (bias === 'REDUCE_SIZE')    return `LONG_PREMIUM + REDUCE_SIZE: adds scaled to ${Math.round((1 - hedgePct) * 100)}% of normal size.`;
      if (bias === 'CONVERT_TO_SPREAD') return 'LONG_PREMIUM: adds disabled after spread conversion.';
      return 'LONG_PREMIUM: pyramiding active — adds allowed per confidence and regime thresholds.';
    }
    return 'Standard threshold-based pyramiding rules apply.';
  };

  if (!matchedEval) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: T.slate, fontSize: '12px', fontFamily: T.font }}>
        No eval context — management rules cannot be determined.
      </div>
    );
  }

  return (
    <div>
      <SLabel>Active Rules</SLabel>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' }}>
        {rules.map(r => (
          <div key={r.label} style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            padding: '8px 12px', borderRadius: '6px',
            border: `1px solid ${r.on ? r.ac + '40' : T.border}`,
            backgroundColor: r.on ? r.ac + '0d' : 'transparent',
          }}>
            <div style={{
              width: '7px', height: '7px', borderRadius: '50%', flexShrink: 0,
              backgroundColor: r.on ? r.ac : T.muted,
              boxShadow: r.on ? `0 0 5px ${r.ac}` : 'none',
            }} />
            <span style={{ fontSize: '11px', color: r.on ? T.text : T.muted, flex: 1, fontFamily: T.font }}>{r.label}</span>
            <span style={{ fontSize: '10px', fontWeight: 700, color: r.on ? r.ac : T.muted, fontFamily: T.font }}>
              {r.on ? 'ON' : 'OFF'}
            </span>
          </div>
        ))}
      </div>

      <SLabel>Pyramiding</SLabel>
      <Box style={{ marginBottom: '10px' }}>
        <div style={{ fontSize: '11px', color: T.body, lineHeight: '1.6', fontFamily: T.font }}>{pyramidNote()}</div>
      </Box>

      {strat === 'DEBIT_SPREAD' && (
        <>
          <SLabel>Abort Rule</SLabel>
          <Box style={{ marginBottom: '10px' }}>
            <div style={{ fontSize: '11px', color: T.body, fontFamily: T.font }}>
              If short leg fails → remain long-only. Monitor for leg imbalance.
            </div>
          </Box>
        </>
      )}

      {/* BRD-2: Session constraints at eval time */}
      {rc && (rc.priorProceedTaken || !rc.earlyEntryWindowOpen || rc.scaleCap != null || rc.stopFloor != null) && (
        <>
          <SLabel>Session Constraints at Eval Time</SLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {rc.priorProceedTaken && (
              <Box style={{ border: `1px solid ${T.amber}40`, backgroundColor: T.amberBg }}>
                <div style={{ fontSize: '9px', color: T.amber, fontFamily: T.font, fontWeight: 700, marginBottom: '3px', letterSpacing: '0.06em' }}>CAPITAL COMMITTED</div>
                <div style={{ fontSize: '11px', color: T.body, fontFamily: T.font }}>Another PROCEED was already taken this session. Bot entered a second position with this in mind.</div>
              </Box>
            )}
            {!rc.earlyEntryWindowOpen && (
              <Box>
                <div style={{ fontSize: '9px', color: T.muted, fontFamily: T.font, fontWeight: 700, marginBottom: '3px', letterSpacing: '0.06em' }}>EARLY ENTRY WINDOW</div>
                <div style={{ fontSize: '11px', color: T.muted, fontFamily: T.font }}>Early entry window was closed at eval time. Only standard entries qualified.</div>
              </Box>
            )}
            {(rc.scaleCap != null || rc.stopFloor != null) && (
              <Box>
                <div style={{ fontSize: '9px', color: T.muted, fontFamily: T.font, marginBottom: '6px', letterSpacing: '0.06em' }}>REGIME-SET LIMITS</div>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  {rc.scaleCap != null && (
                    <div>
                      <div style={{ fontSize: '9px', color: T.muted, fontFamily: T.font }}>Scale Cap</div>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: T.blue, fontFamily: T.font }}>{rc.scaleCap}×</div>
                    </div>
                  )}
                  {rc.stopFloor != null && (
                    <div>
                      <div style={{ fontSize: '9px', color: T.muted, fontFamily: T.font }}>Stop Floor</div>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: T.amber, fontFamily: T.font }}>{(rc.stopFloor * 100).toFixed(1)}%</div>
                    </div>
                  )}
                  {rc.rrFloor != null && (
                    <div>
                      <div style={{ fontSize: '9px', color: T.muted, fontFamily: T.font }}>RR Floor</div>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: T.cyan, fontFamily: T.font }}>{fmtN(rc.rrFloor, 2)}</div>
                    </div>
                  )}
                </div>
              </Box>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MODAL SHELL
// ─────────────────────────────────────────────────────────────────────────────
const OptionsStrategyPanel = ({ show, onHide, position, matchedEval }) => {
  const [tab, setTab] = useState('context');

  if (!show || !position) return null;

  const isClosed = position.status === 'closed';
  const pnlNum   = parseFloat(position.total_pnl_num || 0);
  const strat    = matchedEval?.eval_agent_story?.chart_analysis?.llm_full_response?.option_strategy;
  const sc       = stratStyle(strat);
  const optType  = (position.option_type || '').toUpperCase();
  const dirColor = position.direction === 'long' ? T.blue : T.amber;

  // BRD-2 for header chips
  const rc = extractRc(matchedEval);

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onHide}
        style={{
          position: 'fixed', inset: 0, zIndex: 1050,
          backgroundColor: 'rgba(0,0,0,0.72)',
        }}
      />

      {/* Modal panel — right drawer style */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 1051,
        width: '520px', maxWidth: '95vw',
        backgroundColor: T.card,
        borderLeft: `1px solid ${T.border2}`,
        display: 'flex', flexDirection: 'column',
        boxShadow: '-8px 0 40px rgba(0,0,0,0.6)',
        fontFamily: T.font,
      }}>

        {/* ── Header ── */}
        <div style={{
          padding: '14px 18px', borderBottom: `1px solid ${T.border}`,
          backgroundColor: T.surface, flexShrink: 0,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: T.text, marginBottom: '4px', fontFamily: T.font }}>
                {position.symbol}
              </div>
              <div style={{ fontSize: '11px', color: T.muted, fontFamily: T.font }}>
                {optType && <span style={{ marginRight: '8px' }}>{optType}</span>}
                {position.strike && <span style={{ marginRight: '8px' }}>${position.strike}</span>}
                {position.expiration && <span>exp {position.expiration}</span>}
              </div>
            </div>
            <button
              onClick={onHide}
              style={{
                background: 'none', border: `1px solid ${T.border}`, borderRadius: '4px',
                color: T.muted, fontSize: '16px', cursor: 'pointer', padding: '2px 8px',
                lineHeight: 1, fontFamily: T.font,
              }}
            >×</button>
          </div>

          {/* Summary chips */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px' }}>
            <Chip
              c={isClosed ? T.slate : T.green}
              bg={isClosed ? T.slateBg : T.greenBg}
              b={isClosed ? T.slateBdr : T.greenBdr}
            >
              {(position.status || '').toUpperCase()}
            </Chip>
            <Chip c={dirColor}>{(position.direction || '').toUpperCase()}</Chip>
            <Chip>{v(position.quantity)} contracts</Chip>
            <Chip
              c={PnLColor(pnlNum)}
              bg={pnlNum >= 0 ? T.greenBg : T.redBg}
              b={pnlNum >= 0 ? T.greenBdr : T.redBdr}
            >
              P&L ${fmtN(position.total_pnl_num)}
            </Chip>
            {strat && (
              <Chip c={sc.c} bg={sc.bg} b={sc.b}>{strat}</Chip>
            )}
            {/* BRD-2: Day type in header */}
            {rc?.dayType && (
              <Chip
                c={dayTypeColor(rc.dayType)}
                bg={dayTypeColor(rc.dayType) + '18'}
                b={dayTypeColor(rc.dayType) + '40'}
              >
                {rc.dayType.replace(/_/g, ' ')}
              </Chip>
            )}
            {rc?.eventDay && (
              <Chip c={T.cyan} bg={T.cyanBg} b={T.cyanBdr}>EVENT DAY</Chip>
            )}
          </div>
        </div>

        {/* ── Tab bar ── */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${T.border}`, flexShrink: 0, backgroundColor: T.card }}>
          {['context', 'execution', 'management'].map(t => (
            <TabBtn
              key={t}
              label={t}
              active={tab === t}
              onClick={() => setTab(t)}
            />
          ))}
        </div>

        {/* ── Tab content ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px', backgroundColor: T.bg }}>
          {tab === 'context'    && <ContextTab    position={position} matchedEval={matchedEval} />}
          {tab === 'execution'  && <ExecutionTab  position={position} />}
          {tab === 'management' && <ManagementTab matchedEval={matchedEval} />}
        </div>
      </div>
    </>
  );
};

export default OptionsStrategyPanel;